import React from 'react';
import { useTranslation } from 'react-i18next';
import { SlaughterForecastService } from './SlaughterForecastService';
import { SlaughterForecastDashboard } from '../models/slaughter-forecast';
import { extractApiErrorMessage, notifications } from '../utils';

interface UseSlaughterForecastResult {
  dashboard: SlaughterForecastDashboard | null;
  isBusy: boolean;
  /** True once a fetch has settled, successfully or not - lets callers tell "empty" from "not asked yet". */
  loaded: boolean;
  /** False when the farm has no slaughter source, or when the fetch failed. */
  isEnabled: boolean;
  reload: () => void;
}

/**
 * Loads the dashboard's slaughter delivery widget.
 *
 * `farmId` picks the source: omitted, the signed-in business unit; set, another farm, which is how the
 * admin farms overview opens a farm without switching the session onto it.
 */
export const useSlaughterForecast = (weeks: number, farmId?: string): UseSlaughterForecastResult => {
  const { t } = useTranslation();
  const [dashboard, setDashboard] = React.useState<SlaughterForecastDashboard | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    const service = new SlaughterForecastService();
    // The period can change while a request is in flight; ignore anything but the newest one.
    let cancelled = false;

    setIsBusy(true);

    service
      .getDashboard(weeks, farmId)
      .then((result) => {
        if (cancelled) return;
        setDashboard(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setDashboard(null);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [weeks, farmId, reloadToken]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  // Unlike the piglet widget, this one is hidden until the answer says otherwise: most farms have no
  // slaughter source, so rendering skeletons first would flash a section open and shut on nearly every load.
  const isEnabled = loaded && (dashboard?.isEnabled ?? false);

  return { dashboard, isBusy, loaded, isEnabled, reload };
};

export default useSlaughterForecast;
